import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from './BookingContext';
import { IoCheckmarkCircleSharp } from 'react-icons/io5';

const ContactDetailsPage = () => {
  const { bookingData, setBookingData } = useBooking();
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [countdown, setCountdown] = useState(10);
  
  const [contactData, setContactData] = useState({
    name: '',
    email: '',
    alternateEmail: '',
    mobile: '',
    specialInstructions: ''
  });

  const [showAlternateEmail, setShowAlternateEmail] = useState(false);

  // Countdown timer for auto-close
  useEffect(() => {
    let timer;
    if (showSuccess && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (showSuccess && countdown === 0) {
      handleAutoClose();
    }
    return () => clearInterval(timer);
  }, [showSuccess, countdown]);

  const handleAutoClose = () => {
    setShowSuccess(false);
    setCountdown(10); // Reset for next time
    navigate('/'); // Redirect to home
  };

  const handleChange = (field, value) => {
    setContactData(prev => ({ ...prev, [field]: value }));
  };

const generateWhatsAppMessage = () => {
  const { selectedVehicle, from, to, pickupDate, returnDate, pickupTime, tripType, totalPrice } = bookingData;
  
  // Format dates nicely
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      // Check if it's already a formatted string like "Fri, 2 Jan"
      if (dateString.includes(',')) return dateString;
      
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      return date.toLocaleDateString('en-IN', { 
        weekday: 'short', 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  // Calculate total days for round trips
  const calculateTotalDays = () => {
    if (tripType === 'roundTrip' && pickupDate && returnDate) {
      try {
        const start = new Date(pickupDate);
        const end = new Date(returnDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return diffDays;
      } catch (e) {
        return 1;
      }
    }
    return 1;
  };

  const totalDays = calculateTotalDays();
  const baseFare = (selectedVehicle?.charges || 0) * totalDays;
  const totalFare = totalPrice || (selectedVehicle?.charges || 0) + (selectedVehicle?.perKmPrice || 0) * (selectedVehicle?.kms || 0);

  // Create vehicle features string
  const vehicleFeatures = selectedVehicle?.features ? 
    selectedVehicle.features.join(', ') : 
    'AC, Comfort Seats';

  return `🎟️ *NEW CAB BOOKING REQUEST* 🎟️

*📋 CUSTOMER DETAILS:*
• 👤 Name: ${contactData.name || 'Not provided'}
• 📧 Email: ${contactData.email || 'Not provided'}
• 📞 Mobile: ${contactData.mobile || 'Not provided'}
${contactData.alternateEmail ? `• 📧 Alternate Email: ${contactData.alternateEmail}\n` : ''}

*🚗 TRIP DETAILS:*
• 📍 From: ${from || 'Not specified'}
• 📍 To: ${to || 'Not specified'}
• 📅 Pickup Date: ${formatDate(pickupDate)}
${tripType === 'roundTrip' && returnDate ? `• 📅 Return Date: ${formatDate(returnDate)}\n` : ''}
• 🕒 Pickup Time: ${pickupTime || '10:00 AM (Default)'}
• 🔄 Trip Type: ${tripType === 'roundTrip' ? 'Round Trip' : 'One Way'}
• 📆 Total Days: ${totalDays} day${totalDays > 1 ? 's' : ''}

*🚘 VEHICLE SELECTED:*
• 🚗 Vehicle: ${selectedVehicle?.name || 'Not selected'}
• 💺 Type: ${selectedVehicle?.type || 'Not specified'}
• ⭐ Rating: ${selectedVehicle?.rating || '5.0'}/5.0
• 🏷️ Category: ${selectedVehicle?.category?.charAt(0).toUpperCase() + selectedVehicle?.category?.slice(1) || 'Standard'}
• ✨ Features: ${vehicleFeatures}

*💰 FARE DETAILS:*
• 🛣️ Extra KM Rate: ₹${selectedVehicle?.postLimit || 0}/km
${totalDays > 1 ? `• 📅 For ${totalDays} days: ₹${baseFare}\n` : ''}

*📝 ADDITIONAL CHARGES (As Applicable):*
• 🚘 Driver Bata: As per company policy
• 🛣️ Toll Tax: As per actual
• ⛽ Fuel: Included in package
• 🌙 Night Charges: Applicable after 10:00 PM
• 🅿️ Parking: Additional
• 🏛️ State Tax: As applicable

*📋 TERMS & CONDITIONS:*
1. 300 km/day limit for outstation trips
2. Office to office km calculation
3. Night charges applicable after 10:00 PM
4. Parking charges additional
5. State tax as applicable
6. Cancellation charges apply as per policy
7. Route deviation charges applicable
8. AC usage as per driver discretion during extreme weather

${contactData.specialInstructions ? `\n*📌 SPECIAL INSTRUCTIONS:*\n${contactData.specialInstructions}\n` : ''}

---
📊 *Booking Reference:* ${new Date().getTime()}
📅 *Submitted:* ${new Date().toLocaleString('en-IN')}
🌐 *Via:* Shree Puja Travels 

*Please confirm availability and respond to customer.*`;
};



  const sendToWhatsApp = () => {
  const businessPhone = '919100390049'; // Your business number
  
  // Create the message
  const message = generateWhatsAppMessage();
  
  // Create WhatsApp URL
  const encodedMessage = encodeURIComponent(message);
  const whatsappUrl = `https://wa.me/${businessPhone}?text=${encodedMessage}`;
  
  // Try to open WhatsApp in a new tab
  const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  
  // If popup blocked, show alert
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    alert('Please allow popups to open WhatsApp or copy the message manually.');
    
    // Option 1: Copy message to clipboard
    navigator.clipboard.writeText(message).then(() => {
      alert('Booking details copied to clipboard! Please paste into WhatsApp manually.');
    }).catch(() => {
      // Option 2: Show message in textarea for manual copy
      const textarea = document.createElement('textarea');
      textarea.value = message;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('Booking details copied to clipboard! Please paste into WhatsApp manually.');
    });
  }
  
  // Log for debugging
  console.log('WhatsApp Message:', message);
  console.log('WhatsApp URL:', whatsappUrl);
};

  const handleProceed = () => {
    if (!contactData.name || !contactData.email || !contactData.mobile) {
      alert('Please fill all required fields (Name, Email, Mobile)');
      return;
    }

    setBookingData(prev => ({
      ...prev,
      ...contactData,
      submittedAt: new Date().toISOString(),
      bookingStatus: 'confirmed'
    }));

    sendToWhatsApp();
    setShowSuccess(true);
    setCountdown(10); // Reset countdown
  };

 const SuccessModal = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    {/* Changed from max-w-md to max-w-lg for medium size */}
    <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
      {/* Success Header - slightly reduced padding */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-5 rounded-t-2xl">
        <div className="flex flex-col items-center justify-center">
          {/* Slightly smaller icon */}
          <IoCheckmarkCircleSharp className="text-5xl mb-2 text-white" />
          <h3 className="text-xl font-bold text-center">Booking Confirmed!</h3>
          <p className="text-green-100 text-center text-sm">Your request has been submitted</p>
          <div className="mt-2 text-green-200 text-xs">
            Redirecting to home in {countdown}s...
          </div>
        </div>
      </div>
      
      {/* Success Content - adjusted padding */}
      <div className="p-5">
        <div className="text-center mb-4">
          <p className="text-gray-700 mb-2 text-sm">
            Thank you <span className="font-bold text-cyan-600">{contactData.name}</span>!
          </p>
          <p className="text-gray-600 text-xs">
            Your booking details have been sent via WhatsApp
          </p>
        </div>
        
        {/* Quick Summary */}
        <div className="bg-gray-50 rounded-xl p-3 mb-4">
          <h4 className="font-bold text-gray-800 mb-2 text-sm">Booking Summary</h4>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-600">Vehicle:</span>
              <span className="font-medium">{bookingData.selectedVehicle?.name || 'Not selected'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Route:</span>
              <span className="font-medium">
                {bookingData.from || 'Not specified'} → {bookingData.to || 'Not specified'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Date:</span>
              <span className="font-medium">{bookingData.pickupDate || 'Not specified'}</span>
            </div>
          </div>
        </div>
        
        {/* Next Steps */}
        <div className="mb-5">
          <h4 className="font-bold text-gray-800 mb-2 text-xs">📋 Next Steps:</h4>
          <ol className="space-y-1 text-xs text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-green-500 font-bold flex-shrink-0">1.</span>
              <span>A WhatsApp window has opened with your booking</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 font-bold flex-shrink-0">2.</span>
              <span>Please send the message to complete booking</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 font-bold flex-shrink-0">3.</span>
              <span>Our team will contact you shortly</span>
            </li>
          </ol>
        </div>
        
        {/* Action Buttons - slightly smaller buttons */}
        <div className="flex flex-col gap-2">
          <button
            onClick={() => {
              setShowSuccess(false);
              navigate('/');
            }}
            className="w-full py-2.5 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-colors text-sm"
          >
            Go to Home Now
          </button>
          <button
            onClick={() => {
              setShowSuccess(false);
              // Stay on current page
            }}
            className="w-full py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-lg transition-colors text-sm"
          >
            Stay on Page (Auto-close in {countdown}s)
          </button>
        </div>
      </div>
    </div>
  </div>
);

  const calculateTotalPrice = () => {
    if (!bookingData.selectedVehicle) return 0;
    const basePrice = bookingData.selectedVehicle.perKmPrice * bookingData.selectedVehicle.kms;
    return Math.round(basePrice + bookingData.selectedVehicle.charges);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {showSuccess && <SuccessModal />}
      
      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* ... rest of your existing JSX remains the same ... */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/vehicle-selection')}
            className="text-gray-600 hover:text-gray-900 text-sm font-medium flex items-center gap-1"
          >
            ← Select Vehicle
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="border-b-2 border-gray-900 pb-3 mb-6">
                <h2 className="text-xl font-bold text-gray-900">CONTACT DETAILS</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Fill your details and we'll send the booking to WhatsApp
                </p>
              </div>

              <div className="space-y-4">
                {/* Form fields remain the same */}
                <div>
                  <label className="block text-gray-700 font-bold mb-1 text-sm">NAME *</label>
                  <input
                    type="text"
                    placeholder="Enter your name here"
                    value={contactData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-transparent outline-none text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-1 text-sm">EMAIL *</label>
                  <div className="flex flex-col gap-2">
                    <input
                      type="email"
                      placeholder="Enter your primary email"
                      value={contactData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-transparent outline-none text-sm"
                      required
                    />
                    
                    {!showAlternateEmail ? (
                      <button 
                        type="button" 
                        onClick={() => setShowAlternateEmail(true)}
                        className="text-gray-900 hover:text-gray-700 font-medium text-sm w-fit"
                      >
                        + Add alternate email
                      </button>
                    ) : (
                      <input
                        type="email"
                        placeholder="Enter alternate email (optional)"
                        value={contactData.alternateEmail}
                        onChange={(e) => handleChange('alternateEmail', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-transparent outline-none text-sm"
                      />
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-1 text-sm">MOBILE *</label>
                  <div className="flex gap-3">
                    <select className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-gray-900 outline-none text-sm">
                      <option>India (+91)</option>
                      <option>USA (+1)</option>
                      <option>UK (+44)</option>
                    </select>
                    <input
                      type="tel"
                      placeholder="Enter your WhatsApp number"
                      value={contactData.mobile}
                      onChange={(e) => handleChange('mobile', e.target.value)}
                      className="flex-1 px-2 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-transparent outline-none text-sm"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    We'll send booking confirmation to this WhatsApp number
                  </p>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-1 text-sm">SPECIAL INSTRUCTIONS (Optional)</label>
                  <textarea
                    placeholder="Any special requirements, pickup/drop specifics, or instructions"
                    value={contactData.specialInstructions}
                    onChange={(e) => handleChange('specialInstructions', e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-transparent outline-none text-sm"
                  />
                </div>

                <button
                  onClick={handleProceed}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 rounded-lg text-base shadow-md hover:shadow-lg transition-all mt-2 flex items-center justify-center gap-2"
                >
                  CONFIRM BOOKING
                </button>
              </div>
            </div>
          </div>

          {/* Booking Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden sticky top-4">
              <div className=" text-white p-4" style={{ backgroundColor: 'rgba(65, 1, 150, 1)' }}>
                <h3 className="text-lg font-bold">BOOKING SUMMARY</h3>
              </div>
              
              <div className="p-4 space-y-3">
                {bookingData.selectedVehicle && (
                  <>
                    <div className="flex justify-between py-2 border-b text-sm">
                      <span className="font-semibold text-gray-700">Vehicle:</span>
                      <span className="text-gray-900">{bookingData.selectedVehicle.name}</span>
                    </div>
                    
                    <div className="flex justify-between py-2 border-b text-sm">
                      <span className="font-semibold text-gray-700">Category:</span>
                      <span className="text-gray-900">{bookingData.selectedVehicle.category}</span>
                    </div>
                    
                    <div className="flex justify-between py-2 border-b text-sm">
                      <span className="font-semibold text-gray-700">Seating:</span>
                      <span className="text-gray-900">{bookingData.selectedVehicle.type.split('|')[0]}</span>
                    </div>
                    
                    <div className="flex justify-between py-2 border-b text-sm">
                      <span className="font-semibold text-gray-700">Route:</span>
                      <span className="text-gray-900">{bookingData.from || 'Not set'} → {bookingData.to || 'Not set'}</span>
                    </div>
                    
                    <div className="flex justify-between py-2 border-b text-sm">
                      <span className="font-semibold text-gray-700">Trip Type:</span>
                      <span className="text-gray-900 capitalize">{bookingData.tripType}</span>
                    </div>
                    
                    <div className="flex justify-between py-2 border-b text-sm">
                      <span className="font-semibold text-gray-700">Pickup Date:</span>
                      <span className="text-gray-900">{bookingData.pickupDate}</span>
                    </div>
                    
                    <div className="flex justify-between py-2 border-b text-sm">
                      <span className="font-semibold text-gray-700">Pickup Time:</span>
                      <span className="text-gray-900">{bookingData.pickupTime}</span>
                    </div>
                    
                    <div className="flex justify-between py-2 border-b text-sm">
                      <span className="font-semibold text-gray-700">KM Included:</span>
                      <span className="text-gray-900">{bookingData.selectedVehicle.kms} km</span>
                    </div>
                    
                    <div className="flex justify-between py-2 border-b text-sm">
                      <span className="font-semibold text-gray-700">Extra KM Rate:</span>
                      <span className="text-gray-900">₹{bookingData.selectedVehicle.postLimit}/km</span>
                    </div>
                  </>
                )}
                
                {/* <div className="flex justify-between py-3 bg-cyan-50 -mx-4 px-4 mt-3">
                  <span className="font-bold text-gray-900 text-base">Total Fare:</span>
                  <span className="font-bold text-cyan-600 text-xl">
                    ₹ {calculateTotalPrice().toLocaleString()}
                  </span>
                </div> */}
              </div>

              <div className="border-t">
                <div className="p-4 bg-gray-50">
                  <h4 className="font-bold text-gray-800 mb-2 text-sm">📋 What will be sent:</h4>
                  <ul className="space-y-1 text-xs text-gray-600">
                    <li className="flex items-start gap-1">
                      <span className="text-green-600 mt-0.5">•</span>
                      <span>Customer contact details</span>
                    </li>
                    <li className="flex items-start gap-1">
                      <span className="text-green-600 mt-0.5">•</span>
                      <span>Complete trip itinerary</span>
                    </li>
                    <li className="flex items-start gap-1">
                      <span className="text-green-600 mt-0.5">•</span>
                      <span>Vehicle details & fare breakdown</span>
                    </li>
                    <li className="flex items-start gap-1">
                      <span className="text-green-600 mt-0.5">•</span>
                      <span>Special instructions (if any)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactDetailsPage;